import React, { useMemo, useState } from "react";
import {
  ArrowUp,
  LoaderCircle,
  MessageCircle,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import {
  classifyCompanionIntent,
  COMPANION_QUICK_ACTIONS,
  formatProblemSolvingSummary,
} from "@/components/daily/companionIntent";
import CompanionWorkoutCalendarAction from "@/components/daily/CompanionWorkoutCalendarAction";
import { buildCompanionWorkoutDraft } from "@/components/daily/companionWorkoutCalendar";
import { loadRoutineSettings } from "@/components/daily/routineSettings";
import { evaluateBodyMassGoal } from "@/components/daily/bodyMassEngine";
import { buildFunctionalBaselineVector } from "@/components/assessment/coreBaselineOverrides";
import { buildRobustNervousSystemProfile, isRobustAssessment } from "@/components/assessment/robustAssessmentEngine";
import { responseMagnitude, changeResponseLabel } from "@/components/daily/adaptiveScore";

const SESSION_KEY = "forr:companion-conversation:v6";

const cleanAssistantText = (value) =>
  String(value || "")
    .replace(/\*\*/g, "")
    .replace(/__+/g, "")
    .replace(/`{1,3}/g, "")
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const starterMessage = (preferredName) => ({
  role: "assistant",
  text: preferredName
    ? `Hi ${preferredName}. Ask why you may be feeling something physically or mentally, how the two can be connected, why today’s suggestion fits, or ask me to build a matched workout.`
    : "Ask why you may be feeling something physically or mentally, how the two can be connected, why today’s suggestion fits, or ask me to build a matched workout.",
});

function getSession(preferredName) {
  try {
    const value = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
    return {
      responseId: typeof value.responseId === "string" ? value.responseId : null,
      messages:
        Array.isArray(value.messages) && value.messages.length
          ? value.messages.map((message) => ({ ...message, text: cleanAssistantText(message.text) }))
          : [starterMessage(preferredName)],
    };
  } catch (_error) {
    return { responseId: null, messages: [starterMessage(preferredName)] };
  }
}

function saveSession(messages, responseId) {
  try {
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ messages: messages.slice(-24), responseId }),
    );
  } catch (_error) {
    // Current conversation still works if session storage is unavailable.
  }
}

const ChatMessage = ({ message, messageIndex, addingWorkoutId, onAddWorkout, onOpenCalendar }) => {
  const user = message.role === "user";
  return (
    <div className={user ? "flex justify-end" : "flex justify-start"}>
      <div className={user ? "max-w-[88%]" : "w-full max-w-[94%]"}>
        <div
          className={
            user
              ? "rounded-[20px] rounded-br-md px-4 py-3 text-sm leading-relaxed text-white"
              : "whitespace-pre-wrap rounded-[20px] rounded-bl-md px-4 py-3 text-sm leading-relaxed forr-text-ink"
          }
          style={
            user
              ? { backgroundColor: "var(--forr-schedule)" }
              : { backgroundColor: "var(--forr-surface-tint)", border: "1px solid var(--forr-line-soft)" }
          }
        >
          {message.text}
        </div>
        {!user && message.calendarWorkout && (
          <CompanionWorkoutCalendarAction
            workout={message.calendarWorkout}
            added={Boolean(message.calendarAdded)}
            adding={addingWorkoutId === message.calendarWorkout.id}
            onAdd={() => onAddWorkout(messageIndex)}
            onOpenCalendar={onOpenCalendar}
          />
        )}
      </div>
    </div>
  );
};

function buildAppContext({
  assessment,
  checkIn,
  plan,
  entries,
  biometrics,
  summary,
  progress,
  upcomingEvents,
  nextEventGuidance,
  calendarWorkoutDraft,
}) {
  const latestCompleted = entries?.find((entry) => entry.completed) || null;
  const identity = assessment?.identity || {};
  const settings = loadRoutineSettings() || {};
  const bodyMassResult = settings.bodyMassNutrition?.enabled
    ? evaluateBodyMassGoal(settings.bodyMassNutrition, identity)
    : null;
  const robustProfile =
    assessment?.nervousSystemProfile ||
    assessment?.profile?.nervousSystemProfile ||
    assessment?.answers?.robustProfile ||
    (isRobustAssessment(assessment?.answers || {})
      ? buildRobustNervousSystemProfile(assessment.answers)
      : null);

  return {
    participant: {
      preferredName: identity.preferredName || null,
      firstName: identity.firstName || null,
      age: Number(identity.age) || null,
      sex: identity.sex || null,
    },
    baseline: {
      profileKey: assessment?.profile?.key || null,
      profileTitle: robustProfile?.headline || assessment?.profile?.title || null,
      completedAt: assessment?.completedAt || null,
      assessmentSchemaVersion: assessment?.schemaVersion || null,
      assessmentModel: assessment?.assessmentModel || null,
      nervousSystemProfile: robustProfile
        ? {
  headline: robustProfile.headline,
  bottomLine: robustProfile.bottomLine,
  vector: robustProfile.vector,
  scores: robustProfile.scores,
  contributors: robustProfile.contributors,
  protectiveFactors: robustProfile.protectiveFactors,
  priorities: robustProfile.priorities,
  noChangeNeeded: robustProfile.noChangeNeeded,
  confidence: robustProfile.confidence,
}
        : null,
      functionalVector: robustProfile?.vector || buildFunctionalBaselineVector(assessment?.answers || {}),
    },
    progress: progress
      ? {
          checkIns: progress.checkInCount,
          completedInputs: progress.completedCount,
          responseObservations: progress.observationsCount,
          patternsIdentified: progress.patternsIdentified,
          activitiesExplored: progress.activitiesExplored,
          daysReturned: progress.daysReturned,
          patterns: progress.patterns.slice(0, 8).map((pattern) => ({
            title: pattern.title,
            state: pattern.state,
            count: pattern.count,
            classification: pattern.classification,
            largerChangeCount: pattern.significantOrLarge,
            noChangeCount: pattern.noChange,
            worseCount: pattern.worse,
          })),
        }
      : null,
    currentState: {
      reportedStates: plan?.currentState?.labels || [],
      system: plan?.currentState?.system || null,
      physiologicalTarget: plan?.currentState?.target || null,
      mobilization: Number(checkIn?.intensity) || null,
      usableEnergy: Number(checkIn?.energy) || null,
      actionPressure: Number(checkIn?.actionPressure) || null,
      behavioralChoice: Number(checkIn?.behavioralChoice) || null,
      bodyResponseIntensity: Number(checkIn?.somaticResponse) || null,
      cognitiveCapture: Number(checkIn?.cognitiveCapture) || null,
      recoveryDifficulty: Number(checkIn?.recoveryDifficulty) || null,
      environment: checkIn?.environment || null,
      minutesAvailable: Number(checkIn?.minutes) || null,
      physicalReadiness: checkIn?.physicalReadiness || null,
      conditioningLevel: checkIn?.conditioningLevel || null,
      hydration: checkIn?.hydration || null,
      nourishment: checkIn?.nourishment || null,
      actionDirection: checkIn?.urge || null,
    },
    stateInterpretation: plan?.stateInterpretation || null,
    stateMatch: plan?.stateMatch || null,
    currentRecommendation: plan
      ? {
          title: plan.immediate?.title || null,
          duration: plan.immediate?.duration || null,
          instruction: plan.immediate?.instruction || null,
          intensity: plan.immediate?.intensity || null,
          readiness: plan.readiness?.title || null,
          preparation: plan.preparation || null,
          cooldown: plan.cooldown || null,
          alternatives: (plan.week?.recommendations || []).slice(0, 5).map((option) => ({
            name: option.activityName,
            version: option.versionOrExercise,
            intensity: option.intensity,
            durationMinutes: option.duration,
            role: option.recommendationRole,
          })),
        }
      : null,
    calendar: {
      upcoming: (upcomingEvents || []).slice(0, 10).map((event) => ({
        id: event.id,
        title: event.title,
        date: event.date,
        time: event.time,
        durationMinutes: event.durationMinutes,
        category: event.category,
        modality: event.modality,
        usualEffort: event.usualEffort,
        source: event.source,
        description: event.description || null,
      })),
      horizons: plan?.stateMatch?.calendarDemand?.horizons || null,
      nextEventGuidance: nextEventGuidance || null,
      workoutDraft: calendarWorkoutDraft
        ? {
            title: calendarWorkoutDraft.title,
            date: calendarWorkoutDraft.date,
            time: calendarWorkoutDraft.time,
            durationMinutes: calendarWorkoutDraft.durationMinutes,
            modality: calendarWorkoutDraft.modality,
            preparation: calendarWorkoutDraft.segments?.preparation || [],
            main: calendarWorkoutDraft.segments?.main || null,
            intensity: calendarWorkoutDraft.segments?.intensity || null,
            cooldown: calendarWorkoutDraft.segments?.cooldown || null,
          }
        : null,
    },
    nutritionBodyMass: bodyMassResult?.enabled
      ? {
          direction: bodyMassResult.direction,
          currentWeight: bodyMassResult.currentWeight,
          targetWeight: bodyMassResult.targetWeight,
          weightUnit: bodyMassResult.weightUnit,
          targetDate: bodyMassResult.targetDate,
          maintenanceCalories: bodyMassResult.maintenanceCalories,
          workingCalories: bodyMassResult.workingCalories,
          energyGapPercent: bodyMassResult.energyGapPercent,
          aggressiveTimeline: bodyMassResult.aggressive,
          staged: bodyMassResult.staged,
          selectedMacros: bodyMassResult.selectedMacros,
          observedEnergyEffect: settings.bodyMassNutrition?.energyEffect || null,
          observedIrritabilityEffect: settings.bodyMassNutrition?.irritabilityEffect || null,
          observedRecoveryEffect: settings.bodyMassNutrition?.recoveryEffect || null,
          professionalPlan: Boolean(settings.bodyMassNutrition?.professionalPlan),
          dietNotes: settings.bodyMassNutrition?.dietNotes || null,
          nervousSystemNotes: bodyMassResult.nervousSystemNotes || [],
        }
      : null,
    latestCompletedResponse: latestCompleted
      ? {
          responseMagnitude: responseMagnitude(latestCompleted) || null,
          responseLabel: responseMagnitude(latestCompleted)
            ? changeResponseLabel(responseMagnitude(latestCompleted))
            : null,
          feltWorse: Boolean(latestCompleted.feltWorse),
          response: latestCompleted.response || null,
          recommendation: latestCompleted.recommendation?.title || null,
          energyBefore: latestCompleted.energyBefore || null,
          actionPressureBefore: latestCompleted.actionPressureBefore || null,
          behavioralChoiceBefore: latestCompleted.behavioralChoiceBefore || null,
        }
      : null,
    participation: {
      thisWeekCheckIns: summary?.week?.length || 0,
      thisWeekCompleted: summary?.completed?.length || 0,
      responseObservations: summary?.responseChecks || 0,
      participationRewardPoints: summary?.points || 0,
    },
    optionalBiometrics: biometrics
      ? {
          restingHeartRate: biometrics.heartRate || biometrics.bpm || null,
          hrvRmssdMs: biometrics.hrvMs || null,
          signalQuality: biometrics.quality || null,
          source: biometrics.source || null,
        }
      : null,
  };
}

const AdaptiveCompanionChatV2 = ({
  assessment,
  checkIn,
  plan,
  entries,
  biometrics,
  summary,
  progress,
  upcomingEvents,
  nextEventGuidance,
  onRematch,
  onAddWorkoutToCalendar,
  onOpenCalendar,
  workoutSourceId,
  embedded = false,
}) => {
  const preferredName = assessment?.identity?.preferredName || "";
  const initial = useMemo(() => getSession(preferredName), [preferredName]);
  const [messages, setMessages] = useState(initial.messages);
  const [responseId, setResponseId] = useState(initial.responseId);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [addingWorkoutId, setAddingWorkoutId] = useState(null);

  const selectedActivities = (assessment?.answers?.schedule?.preferredActivities || []).filter(
    (item) => item && item !== "none",
  );

  const addWorkoutAt = async (messageIndex) => {
    const workout = messages[messageIndex]?.calendarWorkout;
    if (!workout || !onAddWorkoutToCalendar || messages[messageIndex]?.calendarAdded) return;
    setAddingWorkoutId(workout.id);
    setError("");
    try {
      const result = await onAddWorkoutToCalendar(workout);
      const next = messages.map((item, index) =>
        index === messageIndex
          ? { ...item, calendarAdded: Boolean(result?.event || result?.added), calendarEventId: result?.event?.id || workout.id }
          : item,
      );
      setMessages(next);
      saveSession(next, responseId);
    } catch (_error) {
      setError("The workout could not be added to the calendar. Please try again.");
    } finally {
      setAddingWorkoutId(null);
    }
  };

  const addLatestWorkoutFromMessage = async (userMessage) => {
    const latest = [...messages]
      .map((item, index) => ({ item, index }))
      .reverse()
      .find(({ item }) => item.calendarWorkout);
    if (!latest) {
      const next = [...messages, userMessage, { role: "assistant", text: "Ask me to build a workout first. Once the workout card appears, I can add it directly to your APOS calendar." }];
      setMessages(next);
      saveSession(next, responseId);
      return;
    }
    const workout = latest.item.calendarWorkout;
    setAddingWorkoutId(workout.id);
    try {
      const result = await onAddWorkoutToCalendar?.(workout);
      const updated = messages.map((item, index) =>
        index === latest.index ? { ...item, calendarAdded: true, calendarEventId: result?.event?.id || workout.id } : item,
      );
      const next = [...updated, userMessage, { role: "assistant", text: `${workout.title} was added to your APOS calendar for ${workout.date} at ${workout.time}.` }];
      setMessages(next);
      saveSession(next, responseId);
    } catch (_error) {
      setError("I could not add that workout to the calendar. Please try again.");
      setMessages([...messages, userMessage]);
    } finally {
      setAddingWorkoutId(null);
    }
  };

  const submit = async (text) => {
    const clean = text.trim();
    if (!clean || status === "sending") return;
    const userMessage = { role: "user", text: clean };
    const pending = [...messages, userMessage];
    setMessages(pending);
    setMessage("");
    setError("");
    setStatus("sending");

    let planForRequest = plan;
    let deterministicFallback = null;
    const intent = classifyCompanionIntent(clean, {
      currentFallback: plan?.immediate?.fallbackLevel || "primary",
      hasSelectedActivity: selectedActivities.length > 0,
      selectedActivityLabel: plan?.immediate?.activityName || "",
    });

    try {
      if (intent?.type === "calendar-add-workout") {
        await addLatestWorkoutFromMessage(userMessage);
        return;
      }
      if (intent?.type === "rematch" && onRematch) {
        const nextPlan = await onRematch(intent);
        if (nextPlan) {
          planForRequest = nextPlan;
          deterministicFallback = formatProblemSolvingSummary(nextPlan);
        }
      }

      const workoutDraft =
        intent?.type === "workout-request"
          ? buildCompanionWorkoutDraft({
              plan: planForRequest,
              checkIn,
              sourceId: `${workoutSourceId || "chat"}-${Date.now()}`,
            })
          : null;

      const result = await fetch("/api/companion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: clean,
          previousResponseId: responseId,
          context: buildAppContext({
            assessment,
            checkIn,
            plan: planForRequest,
            entries,
            biometrics,
            summary,
            progress,
            upcomingEvents,
            nextEventGuidance,
            calendarWorkoutDraft: workoutDraft,
          }),
        }),
      });
      const body = await result.json().catch(() => ({}));
      if (!result.ok) throw new Error(body.error || "Adaptive Companion could not respond right now.");
      const assistantMessage = {
        role: "assistant",
        text: cleanAssistantText(body.message || deterministicFallback || "I could not form a useful response from the available context."),
        ...(workoutDraft ? { calendarWorkout: workoutDraft, calendarAdded: false } : {}),
      };
      const next = [...pending, assistantMessage];
      setMessages(next);
      setResponseId(body.responseId || null);
      saveSession(next, body.responseId || null);
    } catch (requestError) {
      if (deterministicFallback) {
        const next = [...pending, { role: "assistant", text: cleanAssistantText(deterministicFallback) }];
        setMessages(next);
        saveSession(next, responseId);
        setError("The conversational explanation is temporarily unavailable, but your approved option matched to your nervous-system state is ready.");
      } else {
        setError(requestError.message || "Adaptive Companion could not respond right now.");
        setMessages(pending);
        saveSession(pending, responseId);
      }
    } finally {
      setStatus("idle");
    }
  };

  const resetConversation = () => {
    const nextStarter = starterMessage(preferredName);
    setMessages([nextStarter]);
    setResponseId(null);
    setMessage("");
    setError("");
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch (_error) {
      // No action needed.
    }
  };

  return (
    <section
      id="adaptive-companion-chat"
      className={`${embedded ? "" : "mt-10"} overflow-hidden rounded-[30px]`}
      style={{ border: "1px solid var(--forr-action-border)", boxShadow: "var(--forr-shadow-md)" }}
    >
      {!embedded && (
        <div className="p-6 forr-panel-action sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="forr-overline">Adaptive Companion</div>
              <h2 className="mt-2 font-display text-3xl forr-text-ink">Understand what your nervous system is doing</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed forr-text-soft">
                Ask what a physical or mental feeling may mean, how your body and mental experience can be connected, why a state-matched input may help, or request a calendar-ready workout.
              </p>
            </div>
            <Sparkles size={23} className="forr-text-teal-deep" />
          </div>
        </div>
      )}

      <div className="p-4 forr-surface sm:p-6">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div className="font-medium forr-text-ink">Adaptive Companion</div>
          <button type="button" onClick={resetConversation} className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs forr-btn-secondary">
            <RotateCcw size={13} /> New conversation
          </button>
        </div>

        <div
          className="min-h-[300px] max-h-[56vh] space-y-4 overflow-y-auto rounded-[22px] p-4 sm:p-5"
          style={{ backgroundColor: "var(--forr-canvas)", border: "1px solid var(--forr-line-soft)" }}
          aria-live="polite"
        >
          {messages.map((item, index) => (
            <ChatMessage
              key={`${index}-${item.role}`}
              message={item}
              messageIndex={index}
              addingWorkoutId={addingWorkoutId}
              onAddWorkout={addWorkoutAt}
              onOpenCalendar={onOpenCalendar}
            />
          ))}
          {status === "sending" && (
            <div className="flex items-center gap-2 text-sm forr-text-mute">
              <LoaderCircle size={16} className="animate-spin" /> Considering your profile, current response, schedule, and history…
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {COMPANION_QUICK_ACTIONS.map((action) => (
            <button
              type="button"
              key={action.key}
              onClick={() => submit(action.prompt)}
              disabled={status === "sending"}
              className="rounded-full px-3.5 py-2 text-xs forr-surface-tint forr-text-teal-deep disabled:opacity-50"
              style={{ border: "1px solid var(--forr-line-soft)" }}
            >
              {action.label}
            </button>
          ))}
        </div>

        <form
          className="mt-4"
          onSubmit={(event) => {
            event.preventDefault();
            submit(message);
          }}
        >
          <div className="flex items-end gap-3 rounded-[22px] bg-white p-3" style={{ border: "1px solid var(--forr-line)" }}>
            <MessageCircle size={19} className="mb-2.5 ml-1 shrink-0 forr-text-teal-deep" />
            <textarea
              rows="2"
              maxLength="1200"
              value={message}
              disabled={status === "sending"}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Why do I feel this way physically or mentally?"
              className="min-h-[48px] flex-1 resize-none border-0 bg-transparent p-2 text-sm forr-text-ink focus:outline-none"
            />
            <button
              type="submit"
              disabled={!message.trim() || status === "sending"}
              className="mb-1 inline-flex h-10 w-10 items-center justify-center rounded-full forr-btn-primary disabled:opacity-40"
              aria-label="Send message"
            >
              <ArrowUp size={18} />
            </button>
          </div>
        </form>
        {error && <p className="mt-3 text-xs leading-relaxed" style={{ color: "#8a4b3b" }}>{error}</p>}
      </div>
    </section>
  );
};

export default AdaptiveCompanionChatV2;
